import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { siteConfig } from '@/config/site.config'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'

/**
 * The story page. Three chapters, alternating image and text, then the numbers.
 * Long-form and quiet — this is the page that justifies the prices on the
 * other ones.
 */
export default function Maison() {
  const { t, tx } = useI18n()

  const chapters = [
    { title: t('maison.chapter1Title'), body: t('maison.chapter1Body') },
    { title: t('maison.chapter2Title'), body: t('maison.chapter2Body') },
    { title: t('maison.chapter3Title'), body: t('maison.chapter3Body') },
  ]

  return (
    <div data-upbo-id="page-maison" data-upbo-label="Maison page">
      {/* ── Opening statement ──────────────────────────────────────────── */}
      <Container size="md">
        <div className="pt-16 sm:pt-24 pb-14 flex flex-col gap-6 text-center items-center">
          <span className="eyebrow">{t('maison.eyebrow')}</span>
          <h1 className="font-display text-[42px] sm:text-[64px] leading-[1.02] max-w-3xl">
            {t('maison.title')}
          </h1>
          <p className="text-[16px] sm:text-[17px] text-text-muted leading-relaxed max-w-2xl">
            {t('maison.lead')}
          </p>
        </div>
      </Container>

      {/* ── Chapters ───────────────────────────────────────────────────── */}
      <Container size="xl" className="pb-8">
        <div className="flex flex-col gap-16 sm:gap-24">
          {chapters.map((chapter, i) => (
            <article
              key={chapter.title}
              data-upbo-type="section"
              data-upbo-label={`Chapter ${i + 1}`}
              className={cn(
                'grid lg:grid-cols-2 gap-8 lg:gap-16 items-center',
                // Alternate sides so the page reads as a spread, not a list.
                i % 2 === 1 && 'lg:[&>figure]:order-2',
              )}
            >
              <figure className="overflow-hidden rounded-squircle aspect-[4/3] group m-0">
                <img
                  src={siteConfig.maisonImages[i] ?? siteConfig.maisonImages[0]}
                  alt=""
                  loading="lazy"
                  className="img-zoom h-full w-full object-cover"
                />
              </figure>
              <div className="flex flex-col gap-4">
                {/* Sans figures — see the note in StatCard on the serif's "1". */}
                <span className="text-[40px] font-light leading-none tracking-tight text-border-strong">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h2 className="font-display text-[32px] sm:text-[40px] leading-[1.08]">{chapter.title}</h2>
                <p className="text-[15px] text-text-muted leading-relaxed max-w-lg">{chapter.body}</p>
              </div>
            </article>
          ))}
        </div>
      </Container>

      {/* ── The numbers ────────────────────────────────────────────────── */}
      <Container size="xl">
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {siteConfig.maisonStats.map((stat) => (
            <div key={stat.labelKey} className="border-r border-b border-border p-7 sm:p-9 flex flex-col gap-2">
              <span className="text-[40px] sm:text-[52px] font-light leading-none tracking-tight">
                {stat.value}
              </span>
              <span className="text-[10px] uppercase tracking-[0.16em] text-text-muted">{t(stat.labelKey)}</span>
            </div>
          ))}
        </div>
      </Container>

      {/* ── Invitation ─────────────────────────────────────────────────── */}
      <section
        data-upbo-id="maison-cta"
        data-upbo-type="section"
        data-upbo-label="Maison invitation"
        className="mt-20 sm:mt-28 bg-ink text-on-ink"
      >
        <Container size="md">
          <div className="py-20 sm:py-24 flex flex-col items-center text-center gap-5">
            <h2 className="font-display text-[36px] sm:text-[48px] leading-[1.05] max-w-2xl">
              {t('maison.ctaTitle')}
            </h2>
            <p className="text-[15px] text-on-ink/65 max-w-md leading-relaxed">{t('maison.ctaBody')}</p>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-2 text-[12px] text-on-ink/50">
              {siteConfig.salons.map((s) => (
                <span key={tx(s.city)}>
                  {tx(s.city)} · {tx(s.address)}
                </span>
              ))}
            </div>
            <Link to="/contact" className="mt-3">
              <Button size="lg" variant="accent">
                {t('maison.ctaButton')}
              </Button>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  )
}
